import { Component, inject, OnInit, signal } from '@angular/core';
import { ComponentCanDeactivate } from '@uigy/core/guards/component-can-deactivate';
import {
  BulkOtAgentsScheduleEditMutationVariables,
  Capability,
  EditOtAgentMutationVariables,
  ExprOp,
  GetOtAgentsGroupedQuery,
  GetOtAgentsGroupedQueryVariables,
  GetOtAgentsQuery,
  OtAgentAction,
  OtAgentDetailsFragment,
  OtAgentExpressionsParams,
  OtAgentStatus,
} from '@uigy/generated/graphql';
import { GetOtAgentsQueryVariables, OtAgentSelectField } from '@uigy/generated/graphql-worker';
import {
  OtAgentConfigureSliderComponent,
} from '@uigy/modules/ot-agents/components/ot-agent-configure-slider/ot-agent-configure-slider.component';
import {
  ApiKeyGetKeySliderComponent,
} from '@uigy/modules/ot-agents/components/ot-agent-generate-api-key-slider/get-key-step/api-key-get-key-slider.component';
import {
  ApiKeySetDetailsSliderComponent,
} from '@uigy/modules/ot-agents/components/ot-agent-generate-api-key-slider/set-details-step/api-key-set-details-slider.component';
import {
  OtAgentSetScheduleSliderComponent,
} from '@uigy/modules/ot-agents/components/ot-agent-set-schedule-slider/ot-agent-set-schedule-slider.component';
import { OtAgentsGridSettingsService } from '@uigy/modules/ot-agents/services/ot-agents-grid-settings.service';
import { OtAgentsService } from '@uigy/modules/ot-agents/services/ot-agents.service';
import { CapabilitiesService } from '@uigy/shared/capabilities/capabilities.service';
import { SliderSizeEnumV2 } from '@uigy/shared/components/slider-v2/enums/slider-v2.enum';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { WizardPage } from '@uigy/shared/components/wizard/models/wizard-page';
import { WizardConfig } from '@uigy/shared/components/wizard/models/wizard.interfaces';
import { WizardService } from '@uigy/shared/components/wizard/services/wizard.service';
import { TimeUnits } from '@uigy/shared/enums/timeUnits.enum';
import { MaterialModule } from '@uigy/shared/material-module/material.module';
import { ToggleData } from '@uigy/shared/models/interfaces';
import { ConfigService } from '@uigy/shared/services/config.service';
import { LanguageService } from '@uigy/shared/services/language.service';
import { PopupService } from '@uigy/shared/services/popup.service';
import { UiGridComponent } from '@uigy/shared/components/ui-grid/components/ui-grid/ui-grid.component';
import { UiGridIdsEnum } from '@uigy/shared/components/ui-grid/enums/ui-grid-ids.enum';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import { CustomMenuItemParams } from '@uigy/shared/components/ui-grid/models/action-menu';
import { RowsSelection, UiGridOptions } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { I18NextModule } from 'angular-i18next';
import { Subject } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { IemService } from '@uigy/shared/services/iem.service';
import { ActionButtonVariant } from '@uigy/shared/components/ui-buttons-action-bar/models';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { CopyTextRendererComponent } from '@uigy/shared/modules/grid/cell-renderers/copy-text-renderer/copy-text-renderer.component';

@Component({
  selector: 'app-ot-agents',
  standalone: true,
  imports: [
    MaterialModule,
    I18NextModule,
    UiGridComponent,
  ],
  templateUrl: './ot-agents.component.html',
  styleUrl: './ot-agents.component.scss'
})
export class OtAgentsComponent extends ComponentCanDeactivate implements OnInit {
  private languageService = inject(LanguageService);
  private capabilitiesService = inject(CapabilitiesService);
  private otAgentsGridSettingsService = inject(OtAgentsGridSettingsService);
  private otAgentsService = inject(OtAgentsService);
  private wizardService = inject(WizardService);
  private configService = inject(ConfigService);
  private popupService = inject(PopupService);
  private toasterService = inject(ToasterService);
  protected iemService = inject(IemService);

  private getAllOtAgents$ = (pagingParams: GetOtAgentsQueryVariables) => this.otAgentsService.getOtAgents(pagingParams);
  private getAllOtAgentsGrouped$ = (pagingParams: GetOtAgentsGroupedQueryVariables) => this.otAgentsService.getOtAgentsGrouped(pagingParams);

  public readonly otAgentsTexts = this.languageService.texts.ot_agents;
  public readonly gridTitle = this.otAgentsTexts.title;
  public isAgentUpdateCapable: boolean = this.iemService.isIcpViewInEm() ? this.capabilitiesService.capableOf(Capability.ApiKeys) : this.capabilitiesService.capableOf(Capability.WriteOtAgents);

  public autoApproveToggleData = signal<ToggleData>({
    isOn: false,
    switchFunction: this.setAgentsAutoApproveConfig.bind(this),
    isDisabled: !this.isAgentUpdateCapable,
  });
  public autoUpdateToggleData = signal<ToggleData>({
    isOn: false,
    switchFunction: this.openAutoUpdatePopup.bind(this),
    isDisabled: !this.isAgentUpdateCapable,
  });
  public gridOptions?: UiGridOptions<OtAgentDetailsFragment, GetOtAgentsQuery, GetOtAgentsGroupedQuery>;
  private refetchData$ = new Subject<void>();
  protected readonly toasterType = ToasterType;
  private icpAddress: string = '';
  private selectedItemsCount: number = 0;
  private scoutUpdateVersion: string = '';

  ngOnInit(): void {
    this.gridOptions = {
      gridId: UiGridIdsEnum.OT_AGENTS,
      rowSelection: 'multiple',
      gridTitle: this.otAgentsTexts.title,
      dataSource: this.getAllOtAgents$,
      groupingDataSource: this.getAllOtAgentsGrouped$,
      columnDefs: this.otAgentsGridSettingsService.columnDefs,
      actionMenu: this.otAgentsActionMenu(),
      refetchData: this.refetchData$,
      noRowsOverlayComponentParams: {
        title: this.otAgentsTexts.no_results.title,
        items: [
          {
            text: this.otAgentsTexts.no_results.description,
          },
          {
            text: this.otAgentsTexts.no_results.call_for_action,
          },
        ],
      },
      headerOptions: {
        globalButtons: [
          {
            variant: ActionButtonVariant.toggle,
            label: this.otAgentsTexts.toggle_text,
            toggleData: this.autoApproveToggleData,
          },
          {
            variant: ActionButtonVariant.toggle,
            label: this.otAgentsTexts.update_agent.auto_update.toggle_text,
            toggleData: this.autoUpdateToggleData,
          },
          {
            appId: AppIdsEnum.agent_action_key,
            variant: ActionButtonVariant.regular,
            button_variant: 'secondary',
            action: this.generateAPIKey.bind(this),
            label: this.otAgentsTexts.generate_api_key.action_button_title,
          },
        ],
      },
      transformFilterModel: this.otAgentsGridSettingsService.otAgentsTransformFunction,
      activateUrlParams: true,
      components: {
        copyTextRendererComponent: CopyTextRendererComponent,
      },
    };

    this.getAgentsAutoDataConfig();

    // Product requirement: Refetch data every 30 seconds regardless of actions performed by the user.
    setInterval(() => {
      this.refetchData$.next();
      this.getScoutUpdateVersion();
    }, TimeUnits.ThirtySec);
  }

  private otAgentsActionMenu = () => {
    return (rowsSelection: RowsSelection) => {
      {
        if (rowsSelection.isInSelectAllMode || rowsSelection.selectedRows.length > 1) {
          this.selectedItemsCount = rowsSelection.isInSelectAllMode ? rowsSelection.gridApi?.getServerSideGroupLevelState()?.[0]?.info?.totalCount || 0: rowsSelection.gridApi?.getSelectedRows().length;
          return this.getBulkMenuItems(rowsSelection);
        }

        const otAgent = rowsSelection.selectedRows.length === 1 ? rowsSelection.gridApi?.getRowNode(rowsSelection.selectedRows[0])?.data
          : rowsSelection.gridApi?.getSelectedRows()[0] ?? undefined;

        if (!otAgent) {
          return [];
        }

        const otAgentActionBaseFilter: OtAgentExpressionsParams = {
          field: OtAgentSelectField.Id,
          values: [otAgent.id],
          op: ExprOp.In,
        };

        switch (otAgent.status) {
          case OtAgentStatus.Connected:
            return [
              this.useScanNowMenuItem(otAgent, otAgentActionBaseFilter),
              this.useConfigureMenuItem(otAgent),
              this.useUpdateMenuItem(otAgent, otAgentActionBaseFilter),
              this.useDeleteMenuItem(otAgent),
            ];
          case OtAgentStatus.ManuallyRegistered:
            return [
              this.useScanNowMenuItem(otAgent, otAgentActionBaseFilter),
              this.useDeleteMenuItem(otAgent),
            ];
          case OtAgentStatus.PendingApproval:
            return [
              this.useApproveMenuItem(otAgent, otAgentActionBaseFilter),
              this.useDeleteMenuItem(otAgent),
            ];
          case OtAgentStatus.PendingConfiguration:
            return [
              this.useConfigureMenuItem(otAgent),
              this.useUpdateMenuItem(otAgent, otAgentActionBaseFilter),
              this.useDeleteMenuItem(otAgent),
            ];
          case OtAgentStatus.PreparingConnection:
          case OtAgentStatus.WaitingForConnection:
          case OtAgentStatus.Disconnected:
            return [
              this.useDeleteMenuItem(otAgent)
            ];
          case OtAgentStatus.Scanning:
            return [
              this.useAbortScanMenuItem(otAgent, otAgentActionBaseFilter)
            ];
          case OtAgentStatus.Updating:
          default:
            return [];
        }
      }
    };
  };

  private useScanNowMenuItem(otAgent: OtAgentDetailsFragment, filter: OtAgentExpressionsParams): CustomMenuItemParams<OtAgentDetailsFragment> {
    return {
      id: AppIdsEnum.agent_action_scan_now,
      label: this.otAgentsTexts.actions.scan_now,
      onClick: () => this.otAgentsService.otAgentAction({
        filter: filter,
        action: OtAgentAction.LaunchScan
      }).subscribe(() => this.refetchData$.next()),
      capability: Capability.WriteOtAgents,
      disabled: otAgent.networks ? otAgent.networks.length === 0 : true
    };
  }

  private useConfigureMenuItem(otAgent: OtAgentDetailsFragment): CustomMenuItemParams<OtAgentDetailsFragment> {
    return {
      id: AppIdsEnum.agent_action_configure,
      label: this.otAgentsTexts.actions.configure,
      onClick: () => this.openConfigureSlider(otAgent),
      capability: Capability.WriteOtAgents
    };
  }

  private useApproveMenuItem(otAgent: OtAgentDetailsFragment, filter: OtAgentExpressionsParams): CustomMenuItemParams<OtAgentDetailsFragment> {
    return {
      id: AppIdsEnum.agent_action_approve,
      label: this.otAgentsTexts.actions.approve,
      onClick: () => this.otAgentsService.otAgentAction({
        filter: filter,
        action: OtAgentAction.Approve
      }, this.languageService.getTranslationTyped('ot_agents.notifications.scan_approve', {agent_name: otAgent.name})).subscribe(() => this.refetchData$.next()),
      capability: Capability.WriteOtAgents,
    };
  }

  private useUpdateMenuItem(otAgent: OtAgentDetailsFragment, filter: OtAgentExpressionsParams): CustomMenuItemParams<OtAgentDetailsFragment> {
    return {
      id: AppIdsEnum.agent_action_update,
      label: this.otAgentsTexts.actions.update,
      disabled: !otAgent.scoutUpdateAvailable,
      onClick: () => this.popupService.openPopup({
        header: this.otAgentsTexts.update_agent.popup.single.title,
        text: this.languageService.getTranslationTyped('ot_agents.update_agent.popup.single.text', {
          name: otAgent.name,
          version: otAgent.scoutAvailableVersion,
        }),
        submit: {
          title: this.languageService.texts.general.buttons.update,
          execute: () => this.otAgentsService.otAgentAction({
            filter: filter,
            action: OtAgentAction.Update,
          }, this.languageService.getTranslationTyped('ot_agents.update_agent.popup.single.success', { name: otAgent.name }), this.refetchData$).subscribe(() => this.refetchData$.next()),
        },
      }),
      capability: Capability.WriteOtAgents,
    };
  }

  private useDeleteMenuItem(otAgent: OtAgentDetailsFragment): CustomMenuItemParams<OtAgentDetailsFragment> {
    return {
      id: AppIdsEnum.agent_action_delete,
      label: this.otAgentsTexts.actions.delete,
      onClick: () => this.deleteAgent(otAgent),
      capability: Capability.WriteOtAgents,
    };
  }

  private deleteAgent(otAgent: OtAgentDetailsFragment): void {
    this.popupService.openPopup({
      header: this.otAgentsTexts.delete_agent.title,
      text: this.otAgentsTexts.delete_agent.text,
      submit: {
        title: this.languageService.texts.general.buttons.delete,
        execute: () => this.otAgentsService.deleteOtAgent({id: otAgent.id}, this.languageService.getTranslationTyped('ot_agents.notifications.delete', {agent_name: otAgent.name})).subscribe(() => this.refetchData$.next()),
      }
    });
  }

  private useAbortScanMenuItem(otAgent: OtAgentDetailsFragment, filter: OtAgentExpressionsParams): CustomMenuItemParams<OtAgentDetailsFragment> {
    return {
      id: AppIdsEnum.agent_action_abort,
      label: this.otAgentsTexts.actions.abort,
      onClick: () => this.otAgentsService.otAgentAction({
        filter: filter,
        action: OtAgentAction.AbortScan
      }, this.languageService.getTranslationTyped('ot_agents.notifications.scan_aborted', {agent_name: otAgent.name})).subscribe(() => this.refetchData$.next()),
      capability: Capability.WriteOtAgents,
    };
  }

  private getBulkMenuItems(rowsSelection: RowsSelection): CustomMenuItemParams<OtAgentDetailsFragment>[] {
    let selectedAgents: OtAgentDetailsFragment[] = [];

    // In select all mode, we don't have the selected rows (we have []),
    // so we need to get the data of all rendered rows.
    // In regular mode, we can just get the selected rows data.
    if (rowsSelection.isInSelectAllMode) {
      rowsSelection.gridApi?.forEachNode(node => node.data && selectedAgents.push(node.data));
    } else {
      selectedAgents = rowsSelection.gridApi?.getSelectedRows() ?? [];
    }

    const hasUpdatingAgent = selectedAgents.some(row => row.status === OtAgentStatus.Updating);
    const hasAgentWithoutUpdate = selectedAgents.some(row => !row.scoutUpdateAvailable);
    const search = rowsSelection?.gridApi?.getQuickFilter();

    // if we are in select all mode or regular mode BE needs to know what ids to change
    let filter: OtAgentExpressionsParams = {
      field: OtAgentSelectField.Id,
      values: rowsSelection.selectedRows,
      op: rowsSelection?.isInSelectAllMode ? ExprOp.NotIn : ExprOp.In,
    };
    // append user filter (and converts to BE filter) to the filter if he applied any
    if (rowsSelection?.gridApi?.getFilterModel()) {
      filter = GridUtils.combineFilters(filter, GridUtils.convertFilters<OtAgentSelectField>(rowsSelection?.gridApi?.getFilterModel(), rowsSelection?.gridApi) ?? undefined);
    }
    return [
      {
        id: AppIdsEnum.agent_action_approve,
        label: this.otAgentsTexts.actions.approve,
        onClick: () => this.otAgentsService.otAgentAction({
          filter,
          search: search,
          action: OtAgentAction.Approve
        }).subscribe(() => this.refetchData$.next()),
        disabled: hasUpdatingAgent
      },
      {
        id: AppIdsEnum.agent_action_scan_now,
        label: this.otAgentsTexts.actions.scan_now,
        onClick: () => this.otAgentsService.otAgentAction({
          filter,
          search: search,
          action: OtAgentAction.LaunchScan
        }).subscribe(() => this.refetchData$.next()),
        disabled: hasUpdatingAgent
      },
      {
        id: AppIdsEnum.agent_action_bulk_enable_schedule_scan,
        label: this.otAgentsTexts.actions.bulk_enable_schedule_scan,
        onClick: () => this.otAgentsService.bulkOtAgentsScheduleEdit({
          filter,
          search: search,
          enable: true
        }, this.languageService.getTranslationTyped('ot_agents.notifications.bulk_schedule_scan_enabled', {count: this.selectedItemsCount})).subscribe(() => this.refetchData$.next()),
        disabled: hasUpdatingAgent
      },
      {
        id: AppIdsEnum.agent_action_bulk_disable_schedule_scan,
        label: this.otAgentsTexts.actions.bulk_disable_schedule_scan,
        onClick: () => this.otAgentsService.bulkOtAgentsScheduleEdit({
          filter,
          search: search,
          enable: false
        }, this.languageService.getTranslationTyped('ot_agents.notifications.bulk_schedule_scan_disabled', {count: this.selectedItemsCount})).subscribe(() => this.refetchData$.next()),
        disabled: hasUpdatingAgent
      },
      {
        id: AppIdsEnum.agent_action_bulk_set_schedule,
        label: this.otAgentsTexts.actions.bulk_set_schedule,
        onClick: () => this.openBulkSetScheduleSlider(filter, search),
        disabled: hasUpdatingAgent
      },
      {
        id: AppIdsEnum.agent_action_update,
        label: this.otAgentsTexts.actions.update,
        onClick: () => this.popupService.openPopup({
          header: this.otAgentsTexts.update_agent.popup.single.title,
          text: this.languageService.getTranslationTyped('ot_agents.update_agent.popup.bulk.text', {
            count: this.selectedItemsCount,
            version: this.scoutUpdateVersion,
          }),
          submit: {
            title: this.languageService.texts.general.buttons.update,
            execute: () => this.otAgentsService.otAgentAction({
              filter: filter,
              action: OtAgentAction.Update,
            }, this.languageService.getTranslationTyped('ot_agents.update_agent.popup.bulk.success', { count: this.selectedItemsCount }), this.refetchData$).subscribe(() => this.refetchData$.next()),
          },
        }),
        capability: Capability.WriteOtAgents,
        disabled: hasUpdatingAgent || hasAgentWithoutUpdate
      },
      {
        id: AppIdsEnum.agent_action_delete,
        label: this.otAgentsTexts.actions.delete,
        onClick: () => this.bulkDeleteAgents(filter, search),
        disabled: hasUpdatingAgent
      },
    ];
  }

  private bulkDeleteAgents(filter: OtAgentExpressionsParams, search: string | undefined): void {
    this.popupService.openPopup({
      header: this.otAgentsTexts.delete_agent.bulk_title,
      text: this.otAgentsTexts.delete_agent.bulk_text,
      submit: {
        title: this.languageService.texts.general.buttons.delete,
        execute: () => this.otAgentsService.bulkDeleteOtAgents({filter, search}).subscribe(() => this.refetchData$.next()),
      }
    });
  }

  public openConfigureSlider(otAgent: OtAgentDetailsFragment): void {
    const config: WizardConfig = {
      data: otAgent,
      header: this.otAgentsTexts.edit_agent.title,
      pages: [new WizardPage(OtAgentConfigureSliderComponent)],
      submitBtnText: this.languageService.texts.general.buttons.save,
      overlay: SliderSizeEnumV2.form,
    };

    this.wizardService.openWizardSlider<EditOtAgentMutationVariables>(config).pipe(
      switchMap((editVars) => this.otAgentsService.editOtAgent(editVars, this.otAgentsTexts.edit_agent.notifications.success)),
      take(1),
      tap(() => this.refetchData$.next()),
    ).subscribe();
  }


  public generateAPIKey() {
    const config: WizardConfig = {
      data: {icpAddress: this.icpAddress},
      header: this.otAgentsTexts.generate_api_key.slider.title,
      pages: [
        new WizardPage(ApiKeySetDetailsSliderComponent, this.otAgentsTexts.generate_api_key.slider.step_1_name),
        new WizardPage(ApiKeyGetKeySliderComponent, this.otAgentsTexts.generate_api_key.slider.step_2_name)
      ],
      submitBtnText: this.languageService.texts.general.buttons.done,
      overlay: SliderSizeEnumV2.form,
    };

    this.wizardService.openWizardSlider(config).pipe(
      take(1),
      tap(() => this.refetchData$.next()),
    ).subscribe();
  }

  private getAgentsAutoDataConfig(): void {
    this.configService.fetchConfig().pipe(
      tap(config => this.icpAddress = config.MgmtServiceNetworkConfig?.Value?.interfaceIpConfigurations[0]?.ipAddress ?? ''),
    ).subscribe(config => {
        this.autoApproveToggleData.update(data => ({
          ...data,
          isOn: config.OtAgentsAutoApprove.Value,
        }));
        this.autoUpdateToggleData.update(data => ({
          ...data,
          isOn: config.OtAgentAutoUpdate.Value,
        }));
      },
    );
  }

  private getScoutUpdateVersion(): void {
    this.otAgentsService.getScoutUpdateVersion().subscribe(version => {
      if (version) {
        this.scoutUpdateVersion = version;
      }
    });
  }

  private setAgentsAutoApproveConfig(value: boolean): void {
    this.configService.updateConfig({OtAgentsAutoApprove: value}).subscribe(
      () => this.getAgentsAutoDataConfig(),
    );
  }

  private setAgentsAutoUpdateConfig(value: boolean): void {
    this.configService.updateConfig({ OtAgentAutoUpdate: value }).pipe(take(1))
      .subscribe({
        next: () => {
          this.toasterService.openToaster({
            type: ToasterType.Success,
            message: value
              ? this.otAgentsTexts.update_agent.auto_update.enable.success
              : this.otAgentsTexts.update_agent.auto_update.disable.success,
          });
          this.getAgentsAutoDataConfig();
        },
        error: error => {
          this.toasterService.openToaster({
            type: ToasterType.Error,
            message: error,
          });
        },
      });
  }

  private openAutoUpdatePopup(value: boolean): void {
    this.popupService.openPopup({
      header: value ? this.otAgentsTexts.update_agent.auto_update.enable.title : this.otAgentsTexts.update_agent.auto_update.disable.title,
      text: value ? this.otAgentsTexts.update_agent.auto_update.enable.text : this.otAgentsTexts.update_agent.auto_update.disable.text,
      submit: {
        execute: () => this.setAgentsAutoUpdateConfig(value),
        title: value ? this.otAgentsTexts.update_agent.auto_update.enable.title : this.otAgentsTexts.update_agent.auto_update.disable.title,
      },
      cancel: {
        title: this.languageService.texts.general.buttons.cancel,
        execute: () => this.handlePopupCancel(value),
      },
    });
  }

  private handlePopupCancel(value: boolean): void {
    if (this.autoUpdateToggleData()) {
      this.autoUpdateToggleData.update(data => ({
        ...data,
        isOn: !value,
      }));
    }
  }

  public openBulkSetScheduleSlider(filter: OtAgentExpressionsParams, search: string | undefined): void {
    const config: WizardConfig = {
      data: {filter, search},
      header: this.languageService.getTranslationTyped('ot_agents.edit_agent.bulk_set_schedule_title', {count: this.selectedItemsCount}),
      pages: [new WizardPage(OtAgentSetScheduleSliderComponent)],
      submitBtnText: this.languageService.texts.general.buttons.save,
      overlay: SliderSizeEnumV2.form,
    };

    this.wizardService.openWizardSlider<BulkOtAgentsScheduleEditMutationVariables>(config).pipe(
      switchMap((scheduleVars) => this.otAgentsService.bulkOtAgentsScheduleEdit(scheduleVars)),
      take(1),
      tap(() => this.refetchData$.next()),
    ).subscribe();
  }

}
